import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Target, HandHeart, Trophy, Plus, CheckCircle, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HelpAlmaMaterDialog from "@/components/HelpAlmaMaterDialog";
import AlmaMaterPromo from "@/components/AlmaMaterPromo";

const STATUS_META = {
  active: { label: "Active", dot: "🟢" }, urgent: { label: "Urgent", dot: "🔴" },
  partially_funded: { label: "Partially Funded", dot: "🟡" }, fully_funded: { label: "Fully Funded", dot: "🔵" },
  completed: { label: "Completed", dot: "⚪" },
};

function ProjectCard({ p }) {
  const s = STATUS_META[p.status] || STATUS_META.active;
  return (
    <Link to={`/projects/${p.id}`} data-testid={`need-card-${p.id}`}
      className="block rounded-xl border border-border/60 bg-white p-4 transition-all hover:border-secondary/50 hover:shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="rounded-full">{p.category}</Badge>
            <span>{s.dot} {s.label}</span>
          </div>
          <h4 className="mt-1 font-medium leading-tight">{p.title}</h4>
        </div>
      </div>
      {p.description && <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{p.description}</p>}
      {p.funding_goal > 0 && (
        <div className="mt-3">
          <Progress value={p.funded_pct} className="h-2" />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span className="font-medium text-foreground">${(p.amount_pledged || 0).toLocaleString()} pledged</span>
            <span>{p.funded_pct}% of ${p.funding_goal.toLocaleString()}</span>
          </div>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(p.impact_badges || []).map((b) => (
          <span key={b} className="rounded-full bg-secondary/10 px-2 py-0.5 text-[11px] text-secondary">{b}</span>
        ))}
      </div>
    </Link>
  );
}

function PostUpdateDialog({ instId, onDone }) {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [title, setTitle] = useState(""); const [category, setCategory] = useState("Other initiative");
  const [body, setBody] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && !cats.length) api.get("/impact/taxonomy").then((r) => setCats(r.data.update_categories || [])).catch(() => {}); }, [open, cats.length]);
  const submit = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    setBusy(true);
    try { await api.post(`/institutions/${instId}/updates`, { title, category, body }); toast.success("Update posted"); setOpen(false); setTitle(""); setBody(""); onDone && onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1.5" data-testid="post-update-btn"><Plus size={15} /> Post update</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="post-update-dialog">
        <DialogHeader><DialogTitle>What's happening?</DialogTitle><DialogDescription>Share a program, initiative or news with your alumni.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="update-title" /></div>
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="update-category"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Details</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="update-body" /></div>
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="update-submit">{busy ? "Posting…" : "Post"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PostNeedDialog({ instId, onDone }) {
  const [open, setOpen] = useState(false);
  const [tax, setTax] = useState({ need_categories: {}, statuses: [] });
  const [f, setF] = useState({ title: "", category: "Academic", description: "", who_benefits: "", students_affected: "", funding_goal: "", deadline: "", status: "active", target_year: "", target_department: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && !Object.keys(tax.need_categories).length) api.get("/impact/taxonomy").then((r) => setTax(r.data)).catch(() => {}); }, [open, tax]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Add a title"); return; }
    setBusy(true);
    try {
      await api.post(`/institutions/${instId}/projects`, {
        ...f, students_affected: parseInt(f.students_affected || "0"), funding_goal: parseFloat(f.funding_goal || "0"),
      });
      toast.success("Need published"); setOpen(false); onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="gap-1.5" data-testid="post-need-btn"><Plus size={15} /> Post a need</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="post-need-dialog">
        <DialogHeader><DialogTitle>Publish a need or project</DialogTitle><DialogDescription>Tell alumni exactly what's needed and who it helps.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input placeholder="e.g. New Computer Lab" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="need-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="need-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(tax.need_categories || {}).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="need-status"><SelectValue /></SelectTrigger>
                <SelectContent>{(tax.statuses || []).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s]?.dot} {STATUS_META[s]?.label || s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="need-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Who benefits</Label><Input placeholder="e.g. 250 students" value={f.who_benefits} onChange={(e) => set("who_benefits", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Students affected</Label><Input type="number" value={f.students_affected} onChange={(e) => set("students_affected", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Funding goal ($, optional)</Label><Input type="number" value={f.funding_goal} onChange={(e) => set("funding_goal", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Deadline (optional)</Label><Input type="date" value={f.deadline} onChange={(e) => set("deadline", e.target.value)} /></div>
          </div>
          <div className="rounded-lg bg-muted/40 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Target a class (optional)</div>
            <p className="text-xs text-muted-foreground">Aim this need at a specific graduating year and/or department — e.g. "1998 Engineering, your lab needs you".</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Graduating year</Label><Input placeholder="e.g. 1998" value={f.target_year} onChange={(e) => set("target_year", e.target.value)} data-testid="need-target-year" /></div>
              <div className="space-y-1.5"><Label>Department</Label><Input placeholder="e.g. Engineering" value={f.target_department} onChange={(e) => set("target_department", e.target.value)} data-testid="need-target-department" /></div>
            </div>
          </div>
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="need-submit">{busy ? "Publishing…" : "Publish need"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageRequestsDialog({ instId, onDone }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get(`/institutions/${instId}/contributions`).then((r) => setItems(r.data)).catch(() => {}); }, [instId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const act = async (cid, status) => {
    try { await api.post(`/contributions/${cid}/verify`, { status }); toast.success(`Marked ${status}`); load(); onDone && onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const pending = items.filter((i) => i.status === "submitted" || i.status === "under_review");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1.5" data-testid="manage-requests-btn"><CheckCircle size={15} /> Alumni offers{pending.length ? ` (${pending.length})` : ""}</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="manage-requests-dialog">
        <DialogHeader><DialogTitle>Alumni offers to help</DialogTitle><DialogDescription>Verify contributions so they earn recognition badges.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No offers yet.</p>}
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-border/60 p-3" data-testid={`contribution-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{c.anonymous ? "Anonymous Alumni Supporter" : c.user_name} <span className="text-xs font-normal text-muted-foreground">· {c.help_label}</span></div>
                  {(c.amount > 0 || c.quantity) && <div className="text-sm text-secondary">{c.amount > 0 ? `$${c.amount.toLocaleString()} pledged` : c.quantity}</div>}
                  {c.message && <div className="mt-0.5 text-sm text-muted-foreground">"{c.message}"</div>}
                </div>
                <Badge variant="outline" className="rounded-full capitalize">{c.status.replace("_", " ")}</Badge>
              </div>
              {(c.status === "submitted" || c.status === "under_review") && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" className="h-7 gap-1" onClick={() => act(c.id, "verified")} data-testid={`verify-${c.id}`}><CheckCircle size={13} /> Verify</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => act(c.id, "completed")}>Mark completed</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => act(c.id, "declined")}>Decline</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// "From Your Alma Mater" — the living bridge section on every institution page.
export default function InstitutionImpact({ inst }) {
  const instId = inst.id;
  const canManage = !!inst.is_manager;
  const [updates, setUpdates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [helpTypes, setHelpTypes] = useState([]);
  const [champions, setChampions] = useState([]);

  const load = useCallback(() => {
    api.get(`/institutions/${instId}/updates`).then((r) => setUpdates(r.data)).catch(() => {});
    api.get(`/institutions/${instId}/projects`).then((r) => setProjects(r.data)).catch(() => {});
    api.get(`/institutions/${instId}/impact-summary`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/institutions/${instId}/champions`).then((r) => setChampions(r.data.champions || [])).catch(() => {});
  }, [instId]);
  useEffect(() => { load(); api.get("/impact/taxonomy").then((r) => setHelpTypes(r.data.help_types || [])).catch(() => {}); }, [load]);

  const removeUpdate = async (uid) => {
    try { await api.delete(`/institutions/${instId}/updates/${uid}`); load(); } catch { toast.error("Could not remove"); }
  };

  return (
    <section className="mt-8 rounded-2xl border border-secondary/20 bg-gradient-to-br from-green-50/60 via-white to-orange-50/40 p-5 sm:p-6" data-testid="institution-impact">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-secondary">From Your Alma Mater</div>
          <h2 className="font-heading text-2xl font-medium">Your Alma Mater Still Needs You</h2>
        </div>
        <HelpAlmaMaterDialog instId={instId} instName={inst.name} label="Help Your Alma Mater" />
      </div>

      {summary?.badges?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="institution-impact-badges">
          {summary.badges.map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-foreground shadow-sm ring-1 ring-secondary/20">
              <span>{b.icon}</span> {b.label}
            </span>
          ))}
        </div>
      )}

      <Tabs defaultValue="happening" className="mt-4">
        <TabsList className="flex-wrap" data-testid="impact-tabs">
          <TabsTrigger value="happening" data-testid="tab-happening"><Megaphone size={15} className="mr-1" /> What's Happening</TabsTrigger>
          <TabsTrigger value="needs" data-testid="tab-needs"><Target size={15} className="mr-1" /> Needs{projects.length ? ` (${projects.length})` : ""}</TabsTrigger>
          <TabsTrigger value="ways" data-testid="tab-ways"><HandHeart size={15} className="mr-1" /> Ways to Help</TabsTrigger>
          <TabsTrigger value="impact" data-testid="tab-impact"><Trophy size={15} className="mr-1" /> Impact</TabsTrigger>
        </TabsList>

        <TabsContent value="happening" className="mt-4 space-y-3">
          {canManage && <div className="flex justify-end"><PostUpdateDialog instId={instId} onDone={load} /></div>}
          {updates.length === 0 && <p className="py-4 text-sm text-muted-foreground">No updates yet. {canManage ? "Post the first one to keep alumni in the loop." : "Check back soon."}</p>}
          {updates.map((u) => (
            <div key={u.id} className="rounded-xl border border-border/60 bg-white p-4" data-testid={`update-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Badge variant="outline" className="rounded-full text-[11px]">{u.category}</Badge>
                  <h4 className="mt-1 font-medium">{u.title}</h4>
                  {u.body && <p className="mt-1 text-sm text-muted-foreground">{u.body}</p>}
                </div>
                {canManage && <button onClick={() => removeUpdate(u.id)} className="text-muted-foreground hover:text-red-600" title="Remove"><Trash size={16} /></button>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="needs" className="mt-4 space-y-3">
          {canManage && <div className="flex justify-end"><PostNeedDialog instId={instId} onDone={load} /></div>}
          {projects.length === 0 && <p className="py-4 text-sm text-muted-foreground">No needs published yet. {canManage ? "Publish what your institution needs." : "Check back soon — or offer help below."}</p>}
          <div className="grid gap-3 sm:grid-cols-2">{projects.map((p) => <ProjectCard key={p.id} p={p} />)}</div>
        </TabsContent>

        <TabsContent value="ways" className="mt-4">
          <p className="text-sm text-muted-foreground">Every form of help counts — pick a way and send your offer. The institution will confirm.</p>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ways-to-help-grid">
            {helpTypes.map((h) => (
              <div key={h.key} className="rounded-xl border border-border/60 bg-white p-3 text-center text-xs font-medium">{h.label}</div>
            ))}
          </div>
          <div className="mt-4"><HelpAlmaMaterDialog instId={instId} instName={inst.name} label="Help Your Alma Mater" /></div>
        </TabsContent>

        <TabsContent value="impact" className="mt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Active needs", value: summary?.active_needs ?? 0 },
              { label: "Projects", value: summary?.total_projects ?? 0 },
              { label: "Verified contributions", value: summary?.verified_contributions ?? 0 },
              { label: "Alumni supporters", value: summary?.supporter_count ?? 0 },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-border/60 bg-white p-4 text-center">
                <div className="font-heading text-2xl font-medium text-secondary">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
          {canManage && <div className="mt-4"><ManageRequestsDialog instId={instId} onDone={load} /></div>}
          <div className="mt-3">
            <Link to={`/institutions/${instId}/report`} className="inline-flex items-center gap-1.5 text-sm text-secondary hover:underline" data-testid="impact-report-link">
              <Trophy size={15} /> View the Alumni Impact Report
            </Link>
          </div>
          {champions.length > 0 && (
            <div className="mt-5" data-testid="institution-champions">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">🌟 Top champions</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {champions.map((c) => (
                  <Link key={c.user_id} to={`/u/${c.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs shadow-sm ring-1 ring-secondary/20 hover:ring-secondary/50">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-muted-foreground">· {c.projects} project{c.projects !== 1 ? "s" : ""} · {c.updates} updates</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlmaMaterPromo compact className="mt-5" />
    </section>
  );
}
